<script setup>
import {
  onMounted,
  computed,
  ref,
  reactive,
  watch,
  onBeforeUnmount,
} from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import {
  useRoute,
  useRouter,
  onBeforeRouteLeave,
  onBeforeRouteUpdate,
} from 'vue-router';
import { useWorkspaceV2 } from 'dashboard/composables/useWorkspaceV2';

import ContactsDetailsLayout from 'dashboard/components-next/Contacts/ContactsDetailsLayout.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import ContactDetails from 'dashboard/components-next/Contacts/Pages/ContactDetails.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import TabBar from 'dashboard/components-next/tabbar/TabBar.vue';
import ContactNotes from 'dashboard/components-next/Contacts/ContactsSidebar/ContactNotes.vue';
import ContactHistory from 'dashboard/components-next/Contacts/ContactsSidebar/ContactHistory.vue';
import ContactKanbanHistory from 'dashboard/components-next/Contacts/ContactsSidebar/ContactKanbanHistory.vue';
import ContactMedia from 'dashboard/components-next/Contacts/ContactsSidebar/ContactMedia.vue';
import ContactMerge from 'dashboard/components-next/Contacts/ContactsSidebar/ContactMerge.vue';
import ContactCustomAttributes from 'dashboard/components-next/Contacts/ContactsSidebar/ContactCustomAttributes.vue';
import AutomationTimeline from 'dashboard/components/widgets/automation/AutomationTimeline.vue';

const store = useStore();
const route = useRoute();
const router = useRouter();
const { isWorkspaceV2 } = useWorkspaceV2();

const contact = useMapGetter('contacts/getContactById');
const uiFlags = useMapGetter('contacts/getUIFlags');

const activeTab = ref('attributes');
const noteDraft = ref('');
const noteDiscard = ref(null);
const sectionErrors = reactive({ notes: '', history: '', attributes: '' });
const sectionLoading = reactive({
  notes: false,
  history: false,
  attributes: false,
});
const contextKey = computed(
  () => `${route.params.accountId}-${route.params.contactId}`
);
let resolveDiscard;
const finishDiscard = allowed => {
  const resolve = resolveDiscard;
  resolveDiscard = null;
  resolve?.(allowed);
};
const guardDraft = (to, from) => {
  if (resolveDiscard) {
    const previous = resolveDiscard;
    resolveDiscard = null;
    previous(false);
    noteDiscard.value?.close();
  }
  if (
    to.params.accountId === from.params.accountId &&
    to.params.contactId === from.params.contactId
  )
    return true;
  if (!isWorkspaceV2.value || !noteDraft.value) return true;
  noteDiscard.value?.open();
  return new Promise(resolve => {
    resolveDiscard = resolve;
  });
};
onBeforeRouteLeave(guardDraft);
onBeforeRouteUpdate(guardDraft);
const beforeUnload = event => {
  if (isWorkspaceV2.value && noteDraft.value) {
    event.preventDefault();
    event.returnValue = '';
  }
};
window.addEventListener('beforeunload', beforeUnload);
onBeforeUnmount(() => {
  window.removeEventListener('beforeunload', beforeUnload);
  resolveDiscard?.(false);
});
const contactMergeRef = ref(null);
const profileError = ref('');
const profileLoading = ref(true);
let profileRequest = 0;

const isMergingContact = computed(() => uiFlags.value.isMerging);
const isUpdatingContact = computed(() => uiFlags.value.isUpdating);

const selectedContact = computed(() => contact.value(route.params.contactId));

const showSpinner = computed(
  () => profileLoading.value || (!isWorkspaceV2.value && isMergingContact.value)
);

const { t } = useI18n();

const CONTACT_TABS_OPTIONS = [
  { key: 'ATTRIBUTES', value: 'attributes' },
  { key: 'HISTORY', value: 'history' },
  { key: 'AUTOMATIONS', value: 'automations' },
  { key: 'KANBAN_HISTORY', value: 'kanban_history' },
  { key: 'NOTES', value: 'notes' },
  { key: 'MEDIA', value: 'media' },
  { key: 'MERGE', value: 'merge' },
];

const tabLabel = key => {
  switch (key) {
    case 'ATTRIBUTES':
      return t('CONTACTS_LAYOUT.SIDEBAR.TABS.ATTRIBUTES');
    case 'HISTORY':
      return t('CONTACTS_LAYOUT.SIDEBAR.TABS.HISTORY');
    case 'AUTOMATIONS':
      return t('CONTACTS_LAYOUT.SIDEBAR.TABS.AUTOMATIONS');
    case 'KANBAN_HISTORY':
      return t('CONTACTS_LAYOUT.SIDEBAR.TABS.KANBAN_HISTORY');
    case 'NOTES':
      return t('CONTACTS_LAYOUT.SIDEBAR.TABS.NOTES');
    case 'MEDIA':
      return t('CONTACTS_LAYOUT.SIDEBAR.TABS.MEDIA');
    case 'MERGE':
      return t('CONTACTS_LAYOUT.SIDEBAR.TABS.MERGE');
    default:
      return key;
  }
};

const tabs = computed(() => {
  return CONTACT_TABS_OPTIONS.map(tab => ({
    label: tabLabel(tab.key),
    value: tab.value,
  }));
});

const activeTabIndex = computed(() => {
  return CONTACT_TABS_OPTIONS.findIndex(v => v.value === activeTab.value);
});

const goToContactsList = () => {
  if (isWorkspaceV2.value) {
    let path = `/app/accounts/${route.params.accountId}/v2/contacts`;
    const query = { ...route.query };
    if (route.params.segmentId) {
      path += `/segments/${encodeURIComponent(route.params.segmentId)}`;
    } else if (route.params.label) {
      path += `/labels/${encodeURIComponent(route.params.label)}`;
    } else if (query.from === 'active') {
      path += '/active';
    }
    delete query.from;
    router.push({ path, query });
    return;
  }
  if (window.history.state?.back || window.history.length > 1) {
    router.back();
  } else {
    router.push(`/app/accounts/${route.params.accountId}/contacts?page=1`);
  }
};

// SiteUp: depois de unificar, abre o contato que ficou, mantendo a origem
// (segmento ou etiqueta) para o voltar continuar funcionando.
const goToMergedContact = primaryContactId => {
  if (!primaryContactId) {
    goToContactsList();
    return;
  }
  const query = { ...route.query };
  delete query.tab;
  router.push({
    name: route.name || 'contacts_edit',
    params: { ...route.params, contactId: primaryContactId },
    query,
  });
  // Sem o workspace V2 a aba não vem da URL: volta para a primeira aqui,
  // senão o contato que ficou abre direto no formulário de unificação.
  activeTab.value = CONTACT_TABS_OPTIONS[0].value;
};

const fetchActiveContact = async () => {
  const id = route.params.contactId;
  if (!id) return;
  const context = contextKey.value;
  profileRequest += 1;
  const token = profileRequest;
  const isCurrent = () =>
    token === profileRequest && context === contextKey.value;
  profileLoading.value = true;
  profileError.value = '';
  try {
    await store.dispatch('contacts/show', { id, throwOnError: true });
    if (isCurrent()) await store.dispatch('contacts/fetchContactableInbox', id);
  } catch {
    if (isCurrent())
      profileError.value = t('WORKSPACE_V2.CONTACT_DETAIL.LOAD_ERROR');
  } finally {
    if (isCurrent()) profileLoading.value = false;
  }
};

const handleTabChange = tab => {
  activeTab.value = tab.value;
  if (isWorkspaceV2.value)
    router.replace({ query: { ...route.query, tab: tab.value } });
};

const loadSection = async section => {
  const context = contextKey.value;
  sectionErrors[section] = '';
  sectionLoading[section] = true;
  const contactId = route.params.contactId;
  try {
    if (section === 'notes')
      await store.dispatch('contactNotes/get', { contactId });
    if (section === 'history')
      await store.dispatch('contactConversations/get', {
        contactId,
        throwOnError: true,
      });
    if (section === 'attributes')
      await store.dispatch('attributes/get', { throwOnError: true });
  } catch {
    if (context === contextKey.value)
      sectionErrors[section] = t('WORKSPACE_V2.CONTACT_DETAIL.LOAD_ERROR');
  } finally {
    if (context === contextKey.value) sectionLoading[section] = false;
  }
};
const moveTab = (event, index) => {
  if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
  event.preventDefault();
  const count = tabs.value.length;
  let next = (index + (event.key === 'ArrowRight' ? 1 : count - 1)) % count;
  if (event.key === 'Home') next = 0;
  if (event.key === 'End') next = count - 1;
  event.currentTarget.parentElement
    .querySelectorAll('[role="tab"]')
    [next]?.focus();
  handleTabChange(tabs.value[next]);
};
watch(
  () => route.query.tab,
  value => {
    if (isWorkspaceV2.value)
      activeTab.value = CONTACT_TABS_OPTIONS.some(tab => tab.value === value)
        ? value
        : 'attributes';
  },
  { immediate: true }
);
watch(
  contextKey,
  () => {
    noteDraft.value = '';
    ['notes', 'history', 'attributes'].forEach(loadSection);
  },
  { immediate: true }
);

const toggleContactBlock = async isBlocked => {
  if (showSpinner.value || profileError.value || isUpdatingContact.value)
    return;
  if (!selectedContact.value?.id) return;
  const ALERT_MESSAGES = {
    success: {
      block: t('CONTACTS_LAYOUT.HEADER.ACTIONS.BLOCK_SUCCESS_MESSAGE'),
      unblock: t('CONTACTS_LAYOUT.HEADER.ACTIONS.UNBLOCK_SUCCESS_MESSAGE'),
    },
    error: {
      block: t('CONTACTS_LAYOUT.HEADER.ACTIONS.BLOCK_ERROR_MESSAGE'),
      unblock: t('CONTACTS_LAYOUT.HEADER.ACTIONS.UNBLOCK_ERROR_MESSAGE'),
    },
  };

  try {
    await store.dispatch(`contacts/update`, {
      id: selectedContact.value.id,
      blocked: !isBlocked,
    });
    useAlert(
      isBlocked ? ALERT_MESSAGES.success.unblock : ALERT_MESSAGES.success.block
    );
  } catch (error) {
    useAlert(
      isBlocked ? ALERT_MESSAGES.error.unblock : ALERT_MESSAGES.error.block
    );
  }
};

onMounted(() => {
  fetchActiveContact();
});
watch(contextKey, () => {
  fetchActiveContact();
});
</script>

<template>
  <div
    data-testid="page-contact-detail"
    class="flex flex-col justify-between flex-1 h-full m-0 overflow-auto bg-n-surface-1"
    :class="{
      '!bg-n-background text-[15px] [&_h1]:text-[26px] [&_h2]:text-xl':
        isWorkspaceV2,
    }"
  >
    <ContactsDetailsLayout
      :button-label="$t('CONTACTS_LAYOUT.HEADER.SEND_MESSAGE')"
      :selected-contact="selectedContact"
      is-detail-view
      :show-pagination-footer="false"
      :is-updating="isUpdatingContact"
      :block-disabled="showSpinner || isMergingContact || Boolean(profileError)"
      @go-to-contacts-list="goToContactsList"
      @toggle-block="toggleContactBlock"
    >
      <div
        v-if="showSpinner"
        class="flex items-center justify-center py-10 text-n-slate-11"
        :class="{ '!min-h-48': isWorkspaceV2 }"
      >
        <Spinner />
      </div>
      <div
        v-else-if="profileError"
        role="alert"
        class="m-5 rounded-xl border border-n-weak p-4 text-sm text-n-ruby-11"
      >
        <p>{{ profileError }}</p>
        <Button
          :label="t('WORKSPACE_V2.CONTACT_DETAIL.RETRY')"
          @click="fetchActiveContact"
        />
      </div>
      <ContactDetails
        v-else-if="selectedContact"
        :key="contextKey"
        :selected-contact="selectedContact"
        @go-to-contacts-list="goToContactsList"
      />
      <template #sidebarHeader>
        <div
          class="px-6 pt-6 pb-3"
          :class="{
            '!border-b !border-n-weak !px-5 !pb-4 !pt-5': isWorkspaceV2,
          }"
        >
          <div
            v-if="isWorkspaceV2"
            role="tablist"
            :aria-label="t('WORKSPACE_V2.CONTACT_DETAIL.SECTIONS')"
            class="flex flex-wrap gap-1"
          >
            <button
              v-for="(tab, index) in tabs"
              :id="`contact-tab-${tab.value}`"
              :key="tab.value"
              type="button"
              role="tab"
              :aria-selected="activeTab === tab.value"
              :tabindex="activeTab === tab.value ? 0 : -1"
              aria-controls="contact-section"
              class="rounded-full px-3 py-2 text-sm focus-visible:ring-2 focus-visible:ring-n-brand"
              :class="
                activeTab === tab.value
                  ? 'bg-n-slate-12 text-n-solid-1'
                  : 'text-n-slate-11 hover:bg-n-alpha-2'
              "
              @keydown="moveTab($event, index)"
              @click="handleTabChange(tab)"
            >
              {{ tab.label }}
            </button>
          </div>
          <TabBar
            v-else
            :tabs="tabs"
            :initial-active-tab="activeTabIndex"
            class="w-full [&>button]:w-full bg-n-alpha-black2"
            :class="{
              '!rounded-xl !bg-transparent [&>button]:min-h-10 [&>button]:text-[13px]':
                isWorkspaceV2,
            }"
            @tab-changed="handleTabChange"
          />
        </div>
      </template>
      <template #sidebar>
        <div
          v-if="showSpinner"
          class="flex items-center justify-center py-10 text-n-slate-11"
        >
          <Spinner />
        </div>
        <section
          v-else-if="!profileError"
          id="contact-section"
          :key="contextKey"
          :role="isWorkspaceV2 ? 'tabpanel' : undefined"
          :aria-labelledby="
            isWorkspaceV2 ? `contact-tab-${activeTab}` : undefined
          "
        >
          <div
            v-if="
              sectionErrors[activeTab] ||
              (activeTab === 'kanban_history' && sectionErrors.history)
            "
            role="alert"
            class="mx-5 mb-4 rounded-xl border border-n-weak p-4 text-sm text-n-ruby-11"
          >
            <p>{{ sectionErrors[activeTab] || sectionErrors.history }}</p>
            <Button
              :label="t('WORKSPACE_V2.CONTACT_DETAIL.RETRY')"
              @click="
                loadSection(
                  activeTab === 'kanban_history' ? 'history' : activeTab
                )
              "
            />
          </div>
          <div
            v-if="sectionLoading[activeTab]"
            role="status"
            class="flex justify-center p-6"
          >
            <Spinner />
          </div>
          <ContactCustomAttributes
            v-if="
              activeTab === 'attributes' &&
              !sectionErrors.attributes &&
              !sectionLoading.attributes
            "
            :selected-contact="selectedContact"
          />
          <ContactNotes
            v-if="activeTab === 'notes'"
            v-model="noteDraft"
            :load-error="sectionErrors.notes"
            :loading="sectionLoading.notes"
          />
          <ContactHistory
            v-if="
              activeTab === 'history' &&
              !sectionErrors.history &&
              !sectionLoading.history
            "
          />
          <div
            v-if="activeTab === 'automations'"
            class="px-6 py-4"
            :class="{ '!px-5 !py-5': isWorkspaceV2 }"
          >
            <AutomationTimeline
              v-if="selectedContact"
              :contact="selectedContact"
              :limit="20"
            />
          </div>
          <ContactKanbanHistory
            v-if="activeTab === 'kanban_history' && !sectionErrors.history"
          />
          <ContactMedia v-if="activeTab === 'media'" />
          <ContactMerge
            v-if="activeTab === 'merge'"
            ref="contactMergeRef"
            :selected-contact="selectedContact"
            @merged="goToMergedContact"
            @reset-tab="handleTabChange(CONTACT_TABS_OPTIONS[0])"
          />
        </section>
      </template>
    </ContactsDetailsLayout>
    <Dialog
      ref="noteDiscard"
      :title="t('WORKSPACE_V2.CONTACT_DETAIL.DISCARD_TITLE')"
      :description="t('WORKSPACE_V2.CONTACT_DETAIL.DISCARD_DESCRIPTION')"
      :confirm-button-label="t('WORKSPACE_V2.CONTACT_DETAIL.DISCARD')"
      @confirm="
        finishDiscard(true);
        noteDiscard?.close();
      "
      @cancel="finishDiscard(false)"
      @close="finishDiscard(false)"
    />
  </div>
</template>
